import { create } from "zustand";
import clsx from "clsx";
import { useEffect } from "react";

type ToastKind = "success" | "error" | "info";

interface ToastLink {
  label: string;
  href: string;
}

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** In-app deep link shown as a button below the message. */
  action?: ToastLink;
  /** External link (opens in new tab) shown below the message. */
  external?: ToastLink;
}

interface ToasterStore {
  toasts: Toast[];
  push: (kind: ToastKind, message: string, opts?: { action?: ToastLink; external?: ToastLink }) => void;
  dismiss: (id: number) => void;
}

let _seq = 0;

export const useToaster = create<ToasterStore>((set) => ({
  toasts: [],
  push: (kind, message, opts) => {
    const id = ++_seq;
    set((s) => ({ toasts: [...s.toasts, { id, kind, message, ...opts }] }));
    // Toasts with action links stay longer so the user has time to click.
    const ttl = opts?.action || opts?.external ? 8000 : 4000;
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), ttl);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function Toaster() {
  const toasts = useToaster((s) => s.toasts);
  const dismiss = useToaster((s) => s.dismiss);
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={clsx(
            "pointer-events-auto rounded-md shadow-lg px-4 py-2.5 text-sm font-medium text-white flex flex-col gap-1.5 min-w-[260px] max-w-md",
            t.kind === "success" && "bg-emerald-600",
            t.kind === "error" && "bg-red-600",
            t.kind === "info" && "bg-slate-700",
          )}
        >
          <div className="flex items-start gap-3">
            <span className="flex-1">{t.message}</span>
            <button onClick={() => dismiss(t.id)} className="opacity-70 hover:opacity-100 shrink-0 mt-0.5">
              ×
            </button>
          </div>
          {(t.action || t.external) && (
            <div className="flex gap-2 flex-wrap">
              {t.action && (
                <a
                  href={t.action.href}
                  onClick={() => dismiss(t.id)}
                  className="text-xs underline opacity-90 hover:opacity-100 whitespace-nowrap"
                >
                  {t.action.label} →
                </a>
              )}
              {t.external && (
                <a
                  href={t.external.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs underline opacity-90 hover:opacity-100 whitespace-nowrap"
                >
                  {t.external.label} ↗
                </a>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Convenience hook for one-off pushes from anywhere. */
export function useToast() {
  const push = useToaster((s) => s.push);
  return {
    success: (msg: string) => push("success", msg),
    error: (msg: string, opts?: { action?: ToastLink; external?: ToastLink }) =>
      push("error", msg, opts),
    info: (msg: string) => push("info", msg),
  };
}

/** Tiny side-effect helper: show toast on mount (useful for one-shot signals). */
export function ToastOnMount({ kind, message }: { kind: ToastKind; message: string }) {
  const push = useToaster((s) => s.push);
  useEffect(() => { push(kind, message); }, []); // eslint-disable-line
  return null;
}
